<div class="w-full max-w-4xl mx-auto p-6">
  @if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-800">{{ error() }}</div>
  }

  <form [formGroup]="form" (ngSubmit)="onSubmit()">
    <p-card>
      <ng-template pTemplate="header">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50 flex items-center gap-3">
          <p-button icon="pi pi-arrow-left" [rounded]="true" [text]="true" severity="secondary" type="button" (onClick)="cancel()" />
          <h2 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
              <i class="pi pi-building-columns"></i>
            </span>
            {{ isEditMode() ? 'Editar cuenta bancaria' : 'Nueva cuenta bancaria' }}
          </h2>
        </div>
      </ng-template>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        <p-floatlabel variant="on" class="w-full">
          <input pInputText id="alias" formControlName="alias" class="w-full" />
          <label for="alias">Alias <span class="text-red-500">*</span></label>
        </p-floatlabel>
        <p-floatlabel variant="on" class="w-full">
          <input pInputText id="bankName" formControlName="bankName" class="w-full" />
          <label for="bankName">Banco <span class="text-red-500">*</span></label>
        </p-floatlabel>
        <p-floatlabel variant="on" class="w-full">
          <input pInputText id="clabe" formControlName="clabe" class="w-full font-mono" maxlength="18" />
          <label for="clabe">CLABE (18 dígitos) <span class="text-red-500">*</span></label>
        </p-floatlabel>
        <p-floatlabel variant="on" class="w-full">
          <p-select
            formControlName="currencyId"
            inputId="currencyId"
            [options]="currencies()"
            optionLabel="code"
            optionValue="id"
            class="w-full" />
          <label for="currencyId">Moneda <span class="text-red-500">*</span></label>
        </p-floatlabel>
        <div class="md:col-span-2 flex items-center gap-3 py-2">
          <p-toggleswitch formControlName="settlementAccount" inputId="settlementAccount" />
          <label for="settlementAccount" class="text-sm text-slate-700">Cuenta de liquidación del proveedor de pagos</label>
        </div>
      </div>

      <div class="mt-8">
        <h3 class="text-sm font-semibold text-slate-800 mb-4">Mapeo del CSV</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <p-floatlabel variant="on"><input pInputText id="dateColumn" formControlName="dateColumn" class="w-full" /><label for="dateColumn">Columna fecha</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="dateFormat" formControlName="dateFormat" class="w-full" /><label for="dateFormat">Formato fecha</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="descriptionColumn" formControlName="descriptionColumn" class="w-full" /><label for="descriptionColumn">Columna concepto</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="referenceColumn" formControlName="referenceColumn" class="w-full" /><label for="referenceColumn">Columna referencia</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="creditColumn" formControlName="creditColumn" class="w-full" /><label for="creditColumn">Columna abono</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="debitColumn" formControlName="debitColumn" class="w-full" /><label for="debitColumn">Columna cargo</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="amountColumn" formControlName="amountColumn" class="w-full" /><label for="amountColumn">Columna importe</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="signColumn" formControlName="signColumn" class="w-full" /><label for="signColumn">Columna signo (+ abono, − cargo)</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="balanceColumn" formControlName="balanceColumn" class="w-full" /><label for="balanceColumn">Columna saldo</label></p-floatlabel>
          <p-floatlabel variant="on">
            <p-inputnumber inputId="headerRow" formControlName="headerRow" [min]="1" [max]="50" [useGrouping]="false" class="w-full" />
            <label for="headerRow">Fila de encabezados</label>
          </p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="counterpartNameColumn" formControlName="counterpartNameColumn" class="w-full" /><label for="counterpartNameColumn">Columna nombre contraparte</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="counterpartClabeColumn" formControlName="counterpartClabeColumn" class="w-full" /><label for="counterpartClabeColumn">Columna CLABE contraparte</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="trackCodeColumn" formControlName="trackCodeColumn" class="w-full" /><label for="trackCodeColumn">Columna clave de rastreo</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="orderingBankColumn" formControlName="orderingBankColumn" class="w-full" /><label for="orderingBankColumn">Columna banco ordenante</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="paymentFormColumn" formControlName="paymentFormColumn" class="w-full" /><label for="paymentFormColumn">Columna forma de pago</label></p-floatlabel>
          <p-floatlabel variant="on"><input pInputText id="delimiter" formControlName="delimiter" class="w-full" /><label for="delimiter">Separador (, ; o vacío = auto)</label></p-floatlabel>
        </div>
      </div>

      <div class="mt-8">
        <h3 class="text-sm font-semibold text-slate-800 mb-1">Alias de forma de pago</h3>
        <p class="text-sm text-slate-600 m-0">
          Asocia el texto del CSV de esta cuenta con una forma de pago.
        </p>
        <div formArrayName="paymentFormAliases" class="mt-4 flex flex-col gap-3">
          @for (group of paymentFormAliases.controls; track $index) {
            <div class="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-start" [formGroupName]="$index">
              <p-floatlabel variant="on" class="w-full">
                <input pInputText [id]="'paymentFormAlias' + $index" formControlName="alias" class="w-full" maxlength="128" />
                <label [for]="'paymentFormAlias' + $index">Texto del CSV</label>
              </p-floatlabel>
              <p-floatlabel variant="on" class="w-full">
                <p-select
                  [inputId]="'paymentFormAliasForm' + $index"
                  formControlName="paymentFormId"
                  [options]="paymentFormOptions()"
                  optionLabel="label"
                  optionValue="id"
                  filter
                  appendTo="body"
                  class="w-full" />
                <label [for]="'paymentFormAliasForm' + $index">Forma de pago</label>
              </p-floatlabel>
              <p-button icon="pi pi-trash" severity="danger" [text]="true" type="button" (onClick)="removePaymentFormAlias($index)" />
            </div>
          }
        </div>
        <p-button class="mt-3" label="Agregar alias" icon="pi pi-plus" severity="secondary" [outlined]="true" type="button" (onClick)="addPaymentFormAlias()" />
      </div>

      <ng-template pTemplate="footer">
        <div class="!mt-4 flex justify-end gap-4 mt-1 !p-4 !-m-5 bg-gray-50 p-2 border-t border-slate-200">
          <p-button label="Cancelar" severity="secondary" type="button" (onClick)="cancel()" />
          <p-button label="Guardar" type="submit" [loading]="loading()" />
        </div>
      </ng-template>
    </p-card>
  </form>
</div>
